import { io } from 'socket.io-client';

/**
 * Configuration et gestion de la connexion WebSocket
 */
class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
  }

  /**
   * Initialiser la connexion WebSocket
   * @param {string} token - Token d'authentification
   */
  connect(token) {
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return this.socket;
    }

    const wsUrl = process.env.REACT_APP_WS_URL || 'http://localhost:5000';

    this.socket = io(wsUrl, {
      auth: {
        token: token || localStorage.getItem('accessToken'),
      },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    // Événements de connexion
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket.id);
      
      // Rejoindre la room de l'organisation
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.organizationId) {
        this.socket.emit('join:organization', { 
          organizationId: user.organizationId 
        });
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 WebSocket reconnected after', attemptNumber, 'attempts');
    });

    return this.socket;
  }

  /**
   * Déconnecter le WebSocket
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.listeners.clear();
      console.log('WebSocket disconnected');
    }
  }

  /**
   * Écouter un événement
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction de callback
   */
  on(event, callback) {
    if (!this.socket) {
      console.error('Socket not connected. Call connect() first.');
      return;
    }

    this.socket.on(event, callback);
    
    // Stocker le listener pour pouvoir le retirer plus tard
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  /**
   * Retirer un listener d'événement
   * @param {string} event - Nom de l'événement
   * @param {Function} callback - Fonction de callback (optionnel)
   */
  off(event, callback) {
    if (!this.socket) return;

    if (callback) {
      this.socket.off(event, callback);
      
      // Retirer du stockage
      const callbacks = this.listeners.get(event);
      if (callbacks) {
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    } else {
      // Retirer tous les listeners de cet événement
      this.socket.off(event);
      this.listeners.delete(event);
    }
  }

  /**
   * Émettre un événement
   * @param {string} event - Nom de l'événement
   * @param {*} data - Données à envoyer
   */
  emit(event, data) {
    if (!this.socket) {
      console.error('Socket not connected. Call connect() first.');
      return;
    }

    this.socket.emit(event, data);
  }

  /**
   * Vérifier si le socket est connecté
   * @returns {boolean}
   */
  isConnected() {
    return this.socket?.connected || false;
  }

  /**
   * Rejoindre une room
   * @param {string} room - Nom de la room
   */
  joinRoom(room) {
    if (!this.socket) {
      console.error('Socket not connected. Call connect() first.');
      return;
    }

    this.socket.emit('join:room', { room });
  }

  /**
   * Quitter une room
   * @param {string} room - Nom de la room
   */
  leaveRoom(room) {
    if (!this.socket) return;

    this.socket.emit('leave:room', { room });
  }

  /**
   * Écouter les notifications
   * @param {Function} callback - Fonction appelée lors d'une nouvelle notification
   */
  onNotification(callback) {
    this.on('notification', callback);
  }

  /**
   * Écouter les nouveaux messages
   * @param {Function} callback - Fonction appelée lors d'un nouveau message
   */
  onNewMessage(callback) {
    this.on('message:new', callback);
  }

  /**
   * Écouter les mises à jour de conversation
   * @param {Function} callback - Fonction appelée lors d'une mise à jour
   */
  onConversationUpdate(callback) {
    this.on('conversation:update', callback);
  }

  /**
   * Écouter les mises à jour d'agent
   * @param {Function} callback - Fonction appelée lors d'une mise à jour
   */
  onAgentUpdate(callback) {
    this.on('agent:update', callback);
  }

  /**
   * Écouter les mises à jour de commande
   * @param {Function} callback - Fonction appelée lors d'une mise à jour
   */
  onOrderUpdate(callback) {
    this.on('order:update', callback);
  }

  /**
   * Écouter la connexion WhatsApp d'un agent
   * @param {Function} callback - Appelé avec { agentId, sessionId, phoneNumber }
   */
  onWhatsappConnected(callback) {
    this.on('whatsapp:connected', callback);
  }

  /**
   * Écouter la déconnexion WhatsApp d'un agent
   * @param {Function} callback - Appelé avec { agentId, sessionId }
   */
  onWhatsappDisconnected(callback) {
    this.on('whatsapp:disconnected', callback);
  }

  /**
   * Retirer le listener de connexion WhatsApp
   */
  offWhatsappConnected(callback) {
    this.off('whatsapp:connected', callback);
  }

  /**
   * Retirer le listener de déconnexion WhatsApp
   */
  offWhatsappDisconnected(callback) {
    this.off('whatsapp:disconnected', callback);
  }
}

// Instance singleton
const socketService = new SocketService();

export default socketService;
